import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { Calendar, Users, TrendingUp, BarChart3, Clock, AlertCircle,Filter,Download,RefreshCw,UserCheck,UserX,UserMinus,ChevronDown,Activity,Target,Award} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';

const AttendanceDashboard = () => {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [attendanceData, setAttendanceData] = useState(null);
  const [classesData, setClassesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filters
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedStream, setSelectedStream] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Set default dates (last 30 days)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  // Fetch classes on mount
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/classes`);
        setClassesData(response.data);
      } catch (error) {
        console.error('Error fetching classes:', error);
      }
    };
    
    if (user?.school_reg) {
      fetchClasses();
    }
  }, [api, user]);

  // Fetch attendance data
  const fetchAttendanceData = async () => {
    if (!startDate || !endDate) return;
    
    setRefreshing(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
      });
      
      // Only add class and stream filters if specific ones are selected
      if (selectedClass !== 'all') {
        params.append('class_id', selectedClass);
      }
      if (selectedStream !== 'all' && selectedClass !== 'all') {
        params.append('stream_id', selectedStream);
      }
      
      const response = await api.get(`/api/v1/attendance/schools/${user.school_id}/daily-records?${params}`);
      setAttendanceData(response.data);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchAttendanceData();
    }
  }, [selectedClass, selectedStream, startDate, endDate]);

  // Get streams for selected class
  const availableStreams = useMemo(() => {
    if (selectedClass === 'all') {
      // Get all streams from all classes
      const allStreams = classesData.reduce((acc, cls) => {
        return acc.concat(cls.streams.map(stream => ({
          ...stream,
          className: cls.name // Add class name for better identification
        })));
      }, []);
      return allStreams;
    }
    const selectedClassData = classesData.find(c => c.id == selectedClass);
    return selectedClassData?.streams || [];
  }, [classesData, selectedClass]);

  // Process data for analytics
  const analytics = useMemo(() => {
    if (!attendanceData?.sessions) return null;

    const sessions = attendanceData.sessions;
    let totalStudents = 0;
    let totalPresent = 0;
    let totalAbsent = 0;
    let totalLate = 0;
    
    const dailyStats = {};
    const studentStats = {};

    sessions.forEach(session => {
      const date = session.date;
      if (!dailyStats[date]) {
        dailyStats[date] = { date, present: 0, absent: 0, late: 0, total: 0 };
      }

      session.attendance.forEach(record => {
        const status = record.status.toLowerCase();
        dailyStats[date][status]++;
        dailyStats[date].total++;
        
        if (!studentStats[record.student_id]) {
          studentStats[record.student_id] = {
            name: record.student_name,
            admission_number: record.admission_number,
            present: 0,
            absent: 0,
            late: 0,
            total: 0
          };
        }
        
        studentStats[record.student_id][status]++;
        studentStats[record.student_id].total++;
      });
    });

    // Calculate totals
    Object.values(dailyStats).forEach(day => {
      totalPresent += day.present;
      totalAbsent += day.absent;
      totalLate += day.late;
      totalStudents = Math.max(totalStudents, day.total);
    });

    const totalRecords = totalPresent + totalAbsent + totalLate;
    const attendanceRate = totalRecords > 0 ? ((totalPresent + totalLate) / totalRecords * 100).toFixed(1) : 0;
    const punctualityRate = totalRecords > 0 ? (totalPresent / totalRecords * 100).toFixed(1) : 0;

    return {
      totalStudents,
      totalPresent,
      totalAbsent,
      totalLate,
      totalRecords,
      attendanceRate: parseFloat(attendanceRate),
      punctualityRate: parseFloat(punctualityRate),
      dailyStats: Object.values(dailyStats).sort((a, b) => new Date(a.date) - new Date(b.date)),
      studentStats: Object.values(studentStats)
    };
  }, [attendanceData]);

  const statusColors = {
    present: '#10B981',
    absent: '#EF4444', 
    late: '#F59E0B'
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = "blue", trend }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className={`text-3xl font-bold text-${color}-600 mb-1`}>{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl bg-${color}-50`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
      {trend && (
        <div className="mt-4 flex items-center">
          <TrendingUp className={`w-4 h-4 ${trend > 0 ? 'text-green-500' : 'text-red-500'} mr-1`} />
          <span className={`text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? '+' : ''}{trend}% from last period
          </span>
        </div>
      )}
    </div>
  );

  const TabButton = ({ id, label, icon: Icon, isActive, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
        isActive 
          ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' 
          : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
      }`}
    >
      <Icon className="w-4 h-4 mr-2" />
      {label}
    </button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading attendance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Attendance Analytics</h1>
              <p className="text-gray-600">
                {selectedClass === 'all' 
                  ? 'All Classes' 
                  : classesData.find(c => c.id == selectedClass)?.name || 'Unknown Class'
                }
                {selectedStream !== 'all' && selectedClass !== 'all' && 
                  ` - ${availableStreams.find(s => s.id == selectedStream)?.name || 'Unknown Stream'}`
                }
                {selectedClass === 'all' && selectedStream !== 'all' &&
                  ` - ${availableStreams.find(s => s.id == selectedStream)?.name || 'Unknown Stream'}`
                }
              </p>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              
              <button
                onClick={fetchAttendanceData}
                disabled={refreshing}
                className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              
              <button className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors">
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="mt-6 p-4 bg-gray-50 rounded-xl grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Class</label>
                <select
                  value={selectedClass}
                  onChange={(e) => {
                    setSelectedClass(e.target.value);
                    // Reset stream to 'all' when class changes
                    setSelectedStream('all');
                  }}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Classes</option>
                  {classesData.map(cls => (
                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stream</label>
                <select
                  value={selectedStream}
                  onChange={(e) => setSelectedStream(e.target.value)}
                  disabled={selectedClass === 'all' && availableStreams.length === 0}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="all">All Streams</option>
                  {availableStreams.map(stream => (
                    <option key={stream.id} value={stream.id}>
                      {selectedClass === 'all' ? `${stream.className} - ${stream.name}` : stream.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Total Students"
              value={analytics.totalStudents}
              subtitle="Enrolled students"
              icon={Users}
              color="blue"
            />
            <StatCard
              title="Attendance Rate"
              value={`${analytics.attendanceRate}%`}
              subtitle={`${analytics.totalPresent + analytics.totalLate} of ${analytics.totalRecords} records`}
              icon={UserCheck}
              color="green"
            />
            <StatCard
              title="Punctuality Rate"
              value={`${analytics.punctualityRate}%`}
              subtitle={`${analytics.totalPresent} on-time arrivals`}
              icon={Clock}
              color="indigo"
            />
            <StatCard
              title="Absenteeism"
              value={analytics.totalAbsent}
              subtitle={`${(analytics.totalAbsent / analytics.totalRecords * 100).toFixed(1)}% absence rate`}
              icon={UserX}
              color="red"
            />
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex flex-wrap gap-2">
              <TabButton
                id="overview"
                label="Overview"
                icon={BarChart3}
                isActive={activeTab === 'overview'}
                onClick={setActiveTab}
              />
              <TabButton
                id="trends"
                label="Trends"
                icon={TrendingUp}
                isActive={activeTab === 'trends'}
                onClick={setActiveTab}
              />
              <TabButton
                id="students"
                label="Students"
                icon={Users}
                isActive={activeTab === 'students'}
                onClick={setActiveTab}
              />
              <TabButton
                id="insights"
                label="Insights"
                icon={Activity}
                isActive={activeTab === 'insights'}
                onClick={setActiveTab}
              />
            </div>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && analytics && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Daily Attendance Chart */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Daily Attendance Overview</h3>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={analytics.dailyStats}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Area type="monotone" dataKey="present" stackId="1" stroke="#10B981" fill="#10B981" />
                          <Area type="monotone" dataKey="late" stackId="1" stroke="#F59E0B" fill="#F59E0B" />
                          <Area type="monotone" dataKey="absent" stackId="1" stroke="#EF4444" fill="#EF4444" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Status Distribution */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Status Distribution</h3>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Present', value: analytics.totalPresent, fill: '#10B981' },
                              { name: 'Late', value: analytics.totalLate, fill: '#F59E0B' },
                              { name: 'Absent', value: analytics.totalAbsent, fill: '#EF4444' }
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          />
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <UserCheck className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-600">{analytics.totalPresent}</p>
                    <p className="text-sm text-green-700">Present</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 text-center">
                    <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-yellow-600">{analytics.totalLate}</p>
                    <p className="text-sm text-yellow-700">Late</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <UserX className="w-8 h-8 text-red-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-600">{analytics.totalAbsent}</p>
                    <p className="text-sm text-red-700">Absent</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <Target className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-600">{analytics.totalRecords}</p>
                    <p className="text-sm text-blue-700">Total Records</p>
                  </div>
                </div>
              </div>
            )}

            {/* Trends Tab */}
            {activeTab === 'trends' && analytics && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Attendance Trends Over Time</h3>
                  <div className="h-96 bg-gray-50 rounded-xl p-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={analytics.dailyStats}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="present" stroke="#10B981" strokeWidth={3} />
                        <Line type="monotone" dataKey="absent" stroke="#EF4444" strokeWidth={3} />
                        <Line type="monotone" dataKey="late" stroke="#F59E0B" strokeWidth={3} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Daily Attendance Rate</h3>
                  <div className="h-64 bg-gray-50 rounded-xl p-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics.dailyStats.map(day => ({
                        ...day,
                        rate: ((day.present + day.late) / day.total * 100).toFixed(1)
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip formatter={(value) => [`${value}%`, 'Attendance Rate']} />
                        <Bar dataKey="rate" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* Students Tab */}
            {activeTab === 'students' && analytics && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Individual Student Performance</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No.</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Present</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Late</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Absent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance Rate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {analytics.studentStats.map((student, index) => {
                        const attendanceRate = ((student.present + student.late) / student.total * 100).toFixed(1);
                        const status = attendanceRate >= 90 ? 'excellent' : attendanceRate >= 80 ? 'good' : attendanceRate >= 70 ? 'average' : 'poor';
                        const statusColors = {
                          excellent: 'text-green-600 bg-green-50',
                          good: 'text-blue-600 bg-blue-50',
                          average: 'text-yellow-600 bg-yellow-50',
                          poor: 'text-red-600 bg-red-50'
                        };
                        
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-medium text-gray-900">{student.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                              {student.admission_number}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                {student.present}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                                {student.late}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                                {student.absent}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-16 h-2 bg-gray-200 rounded-full mr-2">
                                  <div 
                                    className="h-2 bg-blue-600 rounded-full" 
                                    style={{ width: `${attendanceRate}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-medium">{attendanceRate}%</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${statusColors[status]}`}>
                                {status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Insights Tab */}
            {activeTab === 'insights' && analytics && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <Award className="w-6 h-6 text-blue-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">Key Insights</h3>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p className="text-sm text-gray-700">
                          Overall attendance rate of {analytics.attendanceRate}% is {analytics.attendanceRate >= 90 ? 'excellent' : analytics.attendanceRate >= 80 ? 'good' : 'needs improvement'}
                        </p>
                      </div>
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p className="text-sm text-gray-700">
                          {analytics.totalLate} students arrived late, representing {(analytics.totalLate / analytics.totalRecords * 100).toFixed(1)}% of all records
                        </p>
                      </div>
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p className="text-sm text-gray-700">
                          Absenteeism rate of {(analytics.totalAbsent / analytics.totalRecords * 100).toFixed(1)}% requires attention
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <Target className="w-6 h-6 text-green-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">Recommendations</h3>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p className="text-sm text-gray-700">
                          Focus on students with attendance rates below 80%
                        </p>
                      </div>
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p className="text-sm text-gray-700">
                          Implement early intervention for frequently late students
                        </p>
                      </div>
                      <div className="flex items-start">
                        <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p className="text-sm text-gray-700">
                          Consider parent engagement programs to improve attendance
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <UserCheck className="w-8 h-8 text-green-600" />
                      </div>
                      <p className="text-2xl font-bold text-green-600">{analytics.studentStats.filter(s => ((s.present + s.late) / s.total) >= 0.9).length}</p>
                      <p className="text-sm text-gray-600">Students with 90%+ attendance</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <AlertCircle className="w-8 h-8 text-yellow-600" />
                      </div>
                      <p className="text-2xl font-bold text-yellow-600">{analytics.studentStats.filter(s => ((s.present + s.late) / s.total) < 0.8 && ((s.present + s.late) / s.total) >= 0.7).length}</p>
                      <p className="text-sm text-gray-600">Students at risk (70-79%)</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <UserX className="w-8 h-8 text-red-600" />
                      </div>
                      <p className="text-2xl font-bold text-red-600">{analytics.studentStats.filter(s => ((s.present + s.late) / s.total) < 0.7).length}</p>
                      <p className="text-sm text-gray-600">Students with poor attendance</p>
                    </div>
                  </div>
                </div>

                {/* Attendance Patterns */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Attendance Patterns</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Best Performing Days</h4>
                      <div className="space-y-2">
                        {analytics.dailyStats
                          .sort((a, b) => ((b.present + b.late) / b.total) - ((a.present + a.late) / a.total))
                          .slice(0, 3)
                          .map((day, index) => (
                            <div key={day.date} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                              <div>
                                <p className="font-medium text-gray-900">{new Date(day.date).toLocaleDateString()}</p>
                                <p className="text-sm text-gray-600">{day.total} students recorded</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-green-600">{(((day.present + day.late) / day.total) * 100).toFixed(1)}%</p>
                                <p className="text-xs text-green-600">attendance</p>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Days Needing Attention</h4>
                      <div className="space-y-2">
                        {analytics.dailyStats
                          .sort((a, b) => ((a.present + a.late) / a.total) - ((b.present + b.late) / b.total))
                          .slice(0, 3)
                          .map((day, index) => (
                            <div key={day.date} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                              <div>
                                <p className="font-medium text-gray-900">{new Date(day.date).toLocaleDateString()}</p>
                                <p className="text-sm text-gray-600">{day.total} students recorded</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-red-600">{(((day.present + day.late) / day.total) * 100).toFixed(1)}%</p>
                                <p className="text-xs text-red-600">attendance</p>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top and Bottom Performers */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <Award className="w-6 h-6 text-green-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">Top Performers</h3>
                    </div>
                    <div className="space-y-3">
                      {analytics.studentStats
                        .sort((a, b) => ((b.present + b.late) / b.total) - ((a.present + a.late) / a.total))
                        .slice(0, 5)
                        .map((student, index) => (
                          <div key={student.name} className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-sm font-medium text-green-600">#{index + 1}</span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{student.name}</p>
                                <p className="text-xs text-gray-500">{student.admission_number}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">{(((student.present + student.late) / student.total) * 100).toFixed(1)}%</p>
                              <p className="text-xs text-gray-500">{student.present}P {student.late}L {student.absent}A</p>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <AlertCircle className="w-6 h-6 text-red-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">Students Needing Support</h3>
                    </div>
                    <div className="space-y-3">
                      {analytics.studentStats
                        .sort((a, b) => ((a.present + a.late) / a.total) - ((b.present + b.late) / b.total))
                        .slice(0, 5)
                        .map((student, index) => (
                          <div key={student.name} className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                                <AlertCircle className="w-4 h-4 text-red-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{student.name}</p>
                                <p className="text-xs text-gray-500">{student.admission_number}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-red-600">{(((student.present + student.late) / student.total) * 100).toFixed(1)}%</p>
                              <p className="text-xs text-gray-500">{student.present}P {student.late}L {student.absent}A</p>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </div>

                {/* Weekly Summary */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-700">Average Daily Attendance</span>
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                      </div>
                      <p className="text-2xl font-bold text-blue-600">
                        {(analytics.dailyStats.reduce((acc, day) => acc + ((day.present + day.late) / day.total), 0) / analytics.dailyStats.length * 100).toFixed(1)}%
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-green-700">Best Day</span>
                        <Award className="w-4 h-4 text-green-600" />
                      </div>
                      <p className="text-2xl font-bold text-green-600">
                        {(() => {
                          const bestDay = analytics.dailyStats.reduce((best, day) => 
                            ((day.present + day.late) / day.total) > ((best.present + best.late) / best.total) ? day : best
                          );
                          return (((bestDay.present + bestDay.late) / bestDay.total) * 100).toFixed(1);
                        })()}%
                      </p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-yellow-700">Punctuality Rate</span>
                        <Clock className="w-4 h-4 text-yellow-600" />
                      </div>
                      <p className="text-2xl font-bold text-yellow-600">{analytics.punctualityRate}%</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-purple-700">Total Sessions</span>
                        <Calendar className="w-4 h-4 text-purple-600" />
                      </div>
                      <p className="text-2xl font-bold text-purple-600">{attendanceData?.summary?.total_sessions || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* No Data State */}
        {!analytics && !loading && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Available</h3>
            <p className="text-gray-600 mb-6">
              No attendance records found for the selected filters. Please adjust your search criteria or check back later.
            </p>
            <button
              onClick={fetchAttendanceData}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceDashboard;